import React, { useState, useEffect } from "react";
import styles from "./ProductAnalyticsPage.module.scss";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";
import ProductList from "../../../components/site/ProductAnalytics/ProductList";
import ProductAnalyticsFilters from "../../../components/site/ProductAnalyticsFilters/ProductAnalyticsFilters";
import Loading from "../../../components/site/Loading/Loading";
import NoTariffPage from "../NoTariffPage/NoTariffPage";

import { checkAnalyticProfileTariff } from "../../../utils/getProductListCardDetails";
import { useUsageNotification } from "../../../hooks/useUsageNotification";

import dayjs from "dayjs";
import "dayjs/locale/ru";

dayjs.locale("ru");

const ProductAnalyticsPage = () => {
  const location = useLocation();
  const dateCutoff =
    process.env.REACT_APP_DATE_CUTOFF === "one_month" ? 1 : 2;
  const [fromDate, setFromDate] = useState(() =>
    dayjs().subtract(dateCutoff, "month")
  );
  const [toDate, setToDate] = useState(() =>
    dayjs().subtract(dateCutoff, "month")
  );
  const [searchValue, setSearchValue] = useState("");

  const [selectedCategory, setSelectedCategory] = useState([]);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [selectedSubgroup, setSelectedSubgroup] = useState(null);
  const [selectedType, setSelectedType] = useState(null);

  // "Select all (except)" mode per filter (brand + category levels). When
  // true, the filter's selected values are treated as EXCLUSIONS instead of
  // inclusions.
  const [allBrand, setAllBrand] = useState(false);
  const [allCategory, setAllCategory] = useState(false);
  const [allSubcategory, setAllSubcategory] = useState(false);
  const [allSubgroup, setAllSubgroup] = useState(false);
  const [allType, setAllType] = useState(false);
  const [priceMin, setPriceMin] = useState(null);
  const [priceMax, setPriceMax] = useState(null);
  const [sellersMin, setSellersMin] = useState(null);
  const [sellersMax, setSellersMax] = useState(null);
  const [ordersMin, setOrdersMin] = useState(null);
  const [ordersMax, setOrdersMax] = useState(null);
  const [revenueMin, setRevenueMin] = useState(null);
  const [revenueMax, setRevenueMax] = useState(null);

  const [permissionStatus, setPermissionStatus] = useState("loading");

  const [selectedBrand, setSelectedBrand] = useState([]);
  const [selectedName, setSelectedName] = useState([]);

  // Usage notification hook
  const { showUsageInfo } = useUsageNotification("product_analytics");

  const onBrandChange = (selectedOptions) => {
    setSelectedBrand(selectedOptions || []);
  };

  const onNameChange = (selectedOptions) => {
    setSelectedName(selectedOptions || []);
  };

  const onCategoryChange = (selectedOption) => {
    setSelectedCategory(selectedOption || []);
  };

  const [searchTrigger, setSearchTrigger] = useState(0);
  const [isSearching, setIsSearching] = useState(false);

  const [sorting, setSorting] = useState(null);

  const handleCategoryChange = (selectedOption) => {
    setSelectedCategory(selectedOption);
  };

  const handleSubcategoryChange = (selectedOption) => {
    setSelectedSubcategory(selectedOption);
  };

  const handleSubgroupChange = (selectedOption) => {
    setSelectedSubgroup(selectedOption);
  };

  // Toggling "select all" flips the meaning of the filter's selection
  // (include ⇄ exclude), so clear it to avoid reinterpreting stale values.
  const handleAllBrandChange = (checked) => {
    setAllBrand(checked);
    setSelectedBrand([]);
  };

  const handleAllCategoryChange = (checked) => {
    setAllCategory(checked);
    setSelectedCategory([]);
  };

  const handleAllSubcategoryChange = (checked) => {
    setAllSubcategory(checked);
    setSelectedSubcategory(null);
  };

  const handleAllSubgroupChange = (checked) => {
    setAllSubgroup(checked);
    setSelectedSubgroup(null);
  };

  const handleAllTypeChange = (checked) => {
    setAllType(checked);
    setSelectedType(null);
  };

  const handleSearch = () => {
    setIsSearching(true);
    setSearchTrigger((prev) => prev + 1);
  };

  const handleClearSearch = () => {
    setSearchValue("");

    setSelectedName([]);
    setSelectedCategory([]);
    setSelectedSubcategory(null);
    setSelectedSubgroup(null);
    setSelectedType(null);
    setAllBrand(false);
    setAllCategory(false);
    setAllSubcategory(false);
    setAllSubgroup(false);
    setAllType(false);
    setPriceMin(null);
    setPriceMax(null);
    setSellersMin(null);
    setSellersMax(null);
    setOrdersMin(null);
    setOrdersMax(null);
    setRevenueMin(null);
    setRevenueMax(null);
    setSorting(null);
    setSelectedBrand([]);

    setSearchTrigger((prev) => prev + 1);
    setIsSearching(true);
  };

  const handleFilterSearch = () => {
    // Trigger search in ProductList component
    console.log("Поиск button was triggered");
    console.log(selectedBrand);
    console.log(selectedName);
    console.log(selectedCategory);

    setIsSearching(true);
    setSearchTrigger((prev) => prev + 1);

    // Show usage notification after search
    showUsageInfo();
  };

  const handleSearchComplete = () => {
    setIsSearching(false);
  };

  const handleBrandFilter = (brandName) => {
    // Clear all other filters
    setSearchValue("");
    setSelectedName(null);
    setSelectedCategory(null);
    setSelectedSubcategory(null);
    setSelectedSubgroup(null);
    setSelectedType(null);
    setAllBrand(false);
    setAllCategory(false);
    setAllSubcategory(false);
    setAllSubgroup(false);
    setAllType(false);
    setPriceMin(null);
    setPriceMax(null);
    setSellersMin(null);
    setSellersMax(null);
    setOrdersMin(null);
    setOrdersMax(null);
    setRevenueMin(null);
    setRevenueMax(null);
    setSorting(null);

    setSelectedBrand([{ value: brandName, label: brandName }]);

    setSearchTrigger((prev) => prev + 1);
    setIsSearching(true);
  };

  const handleFromDateChange = (date) => {
    if (date && dayjs.isDayjs(date)) {
      setFromDate(date);
      // Ensure fromDate is not after toDate
      if (toDate && date.isAfter(toDate)) {
        setToDate(date);
      }
    }
  };

  const handleToDateChange = (date) => {
    if (date && dayjs.isDayjs(date)) {
      setToDate(date);
      // Ensure toDate is not before fromDate
      if (fromDate && date.isBefore(fromDate)) {
        setFromDate(date);
      }
    }
  };

  const handleYouTubeClick = () => {
    // Replace with actual YouTube video URL
    window.open("https://youtu.be/xbBE0yOeMzg", "_blank");
  };

  useEffect(() => {
    const checkTariff = async () => {
      // const result = await checkAnalyticProfileTariff();
      // console.log("results of Tariff check", result);

      // if (result) {
      setPermissionStatus("granted");
      // } else {
      //   setPermissionStatus("denied");
      // }
    };
    checkTariff();
  }, []);

  // Handle navigation state from brand analytics page
  useEffect(() => {
    if (location.state?.brandFilter && location.state?.shouldSearch) {
      const brandName = location.state.brandFilter;

      // Set the brand filter
      setSelectedBrand([{ value: brandName, label: brandName }]);

      // Trigger search after a short delay to ensure state is set
      setTimeout(() => {
        setSearchTrigger((prev) => prev + 1);
        setIsSearching(true);
      }, 100);
    }
  }, [location.state]);

  return (
    <>
      <Helmet>
        <title>Аналитика товаров</title>
      </Helmet>
      {permissionStatus === "loading" && <Loading />}
      {permissionStatus === "denied" && <NoTariffPage />}

      {permissionStatus === "granted" && (
        <div className="crm demo6 mb-25">
          <div className="container-fluid">
            <div className="row">
              <div className="col-lg-12">
                <div className="breadcrumb-main">
                  <h4 className="breadcrumb-title">Аналитика товаров</h4>
                </div>

                {/* Controls Section */}
                <ProductAnalyticsFilters
                  // Date range
                  fromDate={fromDate}
                  toDate={toDate}
                  onFromDateChange={handleFromDateChange}
                  onToDateChange={handleToDateChange}
                  // Search
                  searchValue={searchValue}
                  onSearchValueChange={setSearchValue}
                  onSearch={handleSearch}
                  onClearSearch={handleClearSearch}
                  // Selection filters
                  selectedBrand={selectedBrand}
                  onBrandChange={onBrandChange}
                  selectedName={selectedName}
                  onNameChange={onNameChange}
                  selectedCategory={selectedCategory}
                  onCategoryChange={handleCategoryChange}
                  selectedSubcategory={selectedSubcategory}
                  onSubcategoryChange={handleSubcategoryChange}
                  selectedSubgroup={selectedSubgroup}
                  onSubgroupChange={handleSubgroupChange}
                  selectedType={selectedType}
                  onTypeChange={setSelectedType}
                  // "Select all (except)" toggles
                  allBrand={allBrand}
                  onAllBrandChange={handleAllBrandChange}
                  allCategory={allCategory}
                  onAllCategoryChange={handleAllCategoryChange}
                  allSubcategory={allSubcategory}
                  onAllSubcategoryChange={handleAllSubcategoryChange}
                  allSubgroup={allSubgroup}
                  onAllSubgroupChange={handleAllSubgroupChange}
                  allType={allType}
                  onAllTypeChange={handleAllTypeChange}
                  // Range filters
                  priceMin={priceMin}
                  priceMax={priceMax}
                  onPriceMinChange={setPriceMin}
                  onPriceMaxChange={setPriceMax}
                  sellersMin={sellersMin}
                  sellersMax={sellersMax}
                  onSellersMinChange={setSellersMin}
                  onSellersMaxChange={setSellersMax}
                  ordersMin={ordersMin}
                  ordersMax={ordersMax}
                  onOrdersMinChange={setOrdersMin}
                  onOrdersMaxChange={setOrdersMax}
                  revenueMin={revenueMin}
                  revenueMax={revenueMax}
                  onRevenueMinChange={setRevenueMin}
                  onRevenueMaxChange={setRevenueMax}
                  // Actions
                  onFilterSearch={handleFilterSearch}
                  onYouTubeClick={handleYouTubeClick}
                />

                {/* Content Section */}
                <ProductList
                  filters={{
                    dateRange: {
                      from: fromDate?.format("YYYY-MM"),
                      to: toDate?.format("YYYY-MM"),
                    },
                    searchValue,
                    brand:
                      !allBrand && selectedBrand?.length > 0
                        ? selectedBrand.map((b) => b.value)
                        : null,
                    excludeBrand:
                      allBrand && selectedBrand?.length > 0
                        ? selectedBrand.map((b) => b.value)
                        : null,
                    name:
                      selectedName?.length > 0
                        ? selectedName.map((b) => b.value)
                        : null,
                    // Each level is EITHER an include list OR (in "select all"
                    // mode) an exclude list — never both.
                    category:
                      !allCategory && selectedCategory?.length > 0
                        ? selectedCategory.map((c) => c.value)
                        : null,
                    excludeCategory:
                      allCategory && selectedCategory?.length > 0
                        ? selectedCategory.map((c) => c.value)
                        : null,
                    subcategory:
                      !allSubcategory && selectedSubcategory?.length > 0
                        ? selectedSubcategory.map((c) => c.value)
                        : null,
                    excludeSubcategory:
                      allSubcategory && selectedSubcategory?.length > 0
                        ? selectedSubcategory.map((c) => c.value)
                        : null,
                    subgroup:
                      !allSubgroup && selectedSubgroup?.length > 0
                        ? selectedSubgroup.map((c) => c.value)
                        : null,
                    excludeSubgroup:
                      allSubgroup && selectedSubgroup?.length > 0
                        ? selectedSubgroup.map((c) => c.value)
                        : null,
                    type:
                      !allType && selectedType?.length > 0
                        ? selectedType.map((c) => c.value)
                        : null,
                    excludeType:
                      allType && selectedType?.length > 0
                        ? selectedType.map((c) => c.value)
                        : null,
                    price: { min: priceMin, max: priceMax },
                    sellers: { min: sellersMin, max: sellersMax },
                    orders: { min: ordersMin, max: ordersMax },
                    revenue: { min: revenueMin, max: revenueMax },
                  }}
                  sorting={sorting}
                  shouldSearch={searchTrigger}
                  onSearchComplete={handleSearchComplete}
                  isSearching={isSearching}
                  onBrandFilter={handleBrandFilter}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProductAnalyticsPage;
